import { CompanyRole } from "../types/CompanyInfo";
import { RoleType } from "../types/enums/RoleType";
import { toJS } from "mobx";
import { BoardMember } from "../types/BoardMember";
import { compact, sortBy } from "lodash";

import { TMessage } from "../i18n/tt";
import { isNotBlank } from "./strings";

export const boardRoles = [
  RoleType.CHAIRPERSON,
  RoleType.DEPUTY_CHAIRPERSON,
  RoleType.MEMBER,
  RoleType.DEPUTY_MEMBER,
];

export const sortedRoleTypes = [
  RoleType.CEO,
  RoleType.CHAIRPERSON,
  RoleType.DEPUTY_CHAIRPERSON,
  RoleType.MEMBER,
  RoleType.DEPUTY_MEMBER,
];

export const extractChairperson = (roles: CompanyRole[]) => {
  const chairperson = rolesByType(toJS(roles), RoleType.CHAIRPERSON);
  if (chairperson.length > 0) {
    return chairperson[0];
  } else {
    return null;
  }
};

export const extractDeputyChairpersonName = (roles: CompanyRole[]) => {
  if (roles && roles.length > 0) {
    const deputyChairperson = roles.find((r) => RoleType.DEPUTY_CHAIRPERSON.valueOf() === r.role);
    if (deputyChairperson) {
      return getFullName(deputyChairperson);
    }
  }
  return "";
};

export const extractAllBoardMembers = (roles: CompanyRole[]) => {
  if (roles && roles.length > 0) {
    return roles
      .filter((r) => [RoleType.MEMBER.valueOf(), RoleType.DEPUTY_MEMBER.valueOf()].includes(r.role))
      .map(getFullName);
  }
  return [];
};

export const extractBoardMembers = (roles: CompanyRole[]) => {
  if (roles && roles.length > 0) {
    return roles.filter((r) => [RoleType.MEMBER.valueOf()].includes(r.role)).map(getFullName);
  }
  return [];
};

export const extractBoardDeputy = (roles: CompanyRole[]) => {
  if (roles && roles.length > 0) {
    return roles
      .filter((r) => [RoleType.DEPUTY_MEMBER.toString()].includes(r.role))
      .map(getFullName);
  }
  return [];
};

export const byName = (a: { name: string }, b: { name: string }) => (a.name > b.name ? 1 : -1);

export const rolesByType = (roles: CompanyRole[], type: RoleType) => {
  const byType = roles.filter((r) => r.role === type.valueOf());
  return byType
    ? byType
        .map<BoardMember>((p) => ({
          name: getFullName(p),
          companyroleId: p.companyroleId,
        }))
        .sort(byName)
    : [];
};

export function getFullName(
  person: { firstName?: string; middleName?: string; lastName?: string } | null | undefined,
) {
  if (!person) return "";

  return compact([person.firstName, person.middleName, person.lastName])
    .map((s) => s.trim())
    .join(" ");
}

export function getFullAddress(
  address: { address?: string; zipCode?: string; city?: string } | undefined,
) {
  if (!address) return "";

  return compact([
    address.address ? `${address.address},` : undefined,
    address.zipCode,
    address.city,
  ])
    .map((s) => s.trim())
    .join(" ");
}

export function sortAndFilterBoardByRole(
  array: CompanyRole[],
  field: keyof CompanyRole,
  sortOrder: string[],
  onlyTheseRoles: RoleType[] = [],
) {
  const ordering: Record<string, number> = {};

  for (let i = 0; i < sortOrder.length; i++) ordering[sortOrder[i]] = i;

  return array
    .filter((role) => (onlyTheseRoles.length ? onlyTheseRoles.includes(role.role) : true))
    .sort(
      (a, b) =>
        ordering[String(a[field])] - ordering[String(b[field])] ||
        getFullName(a).localeCompare(getFullName(b)),
    );
}

export function sortedBoardAndCEO(roles: CompanyRole[]) {
  const sorted = sortedBoard(roles);
  const ceo = roles.find((r) => r.role === RoleType.CEO);
  if (ceo) {
    sorted.push(ceo);
  }
  return sorted;
}

export function sortedBoard(roles: CompanyRole[]) {
  return sortBy(
    roles.filter((r) => isBoardMember(r.role) || isDeputyBoardMember(r.role)),
    (r) => boardRoles.indexOf(r.role),
    (r) => getFullName(r),
  );
}

export function sortedRoles(roles: CompanyRole[]) {
  return sortBy(
    roles,
    (r) => sortedRoleTypes.indexOf(r.role),
    (r) => getFullName(r),
  );
}

/**
 * The term "board member" includes regular board members, chairpersons and deputy
 * chairpersons, but not deputy members.
 */
export function isBoardMember(roleType: RoleType): boolean;
export function isBoardMember(role: { role?: RoleType }): boolean;
export function isBoardMember(roleOrRoleType: { role?: RoleType } | RoleType): boolean {
  const roleType = typeof roleOrRoleType === "object" ? roleOrRoleType.role : roleOrRoleType;
  switch (roleType) {
    case RoleType.CHAIRPERSON:
    case RoleType.DEPUTY_CHAIRPERSON:
    case RoleType.MEMBER:
      return true;
    default:
      return false;
  }
}

export function isDeputyBoardMember(roleType: RoleType): boolean;
export function isDeputyBoardMember(role: { role?: RoleType }): boolean;
export function isDeputyBoardMember(roleOrRoleType: { role?: RoleType } | RoleType): boolean {
  const roleType = typeof roleOrRoleType === "object" ? roleOrRoleType.role : roleOrRoleType;
  return roleType === RoleType.DEPUTY_MEMBER;
}

export function isChairperson(roleType: RoleType): boolean;
export function isChairperson(role: { role?: RoleType }): boolean;
export function isChairperson(roleOrRoleType: { role?: RoleType } | RoleType): boolean {
  const roleType = typeof roleOrRoleType === "object" ? roleOrRoleType.role : roleOrRoleType;
  return roleType === RoleType.CHAIRPERSON;
}

export function isDeputyChairperson(roleType: RoleType): boolean;
export function isDeputyChairperson(role: { role?: RoleType }): boolean;
export function isDeputyChairperson(roleOrRoleType: { role?: RoleType } | RoleType): boolean {
  const roleType = typeof roleOrRoleType === "object" ? roleOrRoleType.role : roleOrRoleType;
  return roleType === RoleType.DEPUTY_CHAIRPERSON;
}

export function isObserver(roleType: RoleType): boolean;
export function isObserver(role: { role?: RoleType }): boolean;
export function isObserver(roleOrRoleType: { role?: RoleType } | RoleType): boolean {
  const roleType = typeof roleOrRoleType === "object" ? roleOrRoleType.role : roleOrRoleType;
  return roleType === RoleType.OBSERVER;
}

export const splitName = (
  name: string,
): {
  firstName: string;
  middleName?: string;
  lastName: string;
} => {
  const splittedName = name.trim().split(/\s+/g);

  if (splittedName.length >= 3)
    return {
      firstName: splittedName[0],
      middleName: splittedName.slice(1, -1).join(" "),
      lastName: splittedName[splittedName.length - 1],
    };

  return { firstName: splittedName[0] ?? "", lastName: splittedName[1] ?? "" };
};

export function validateName(value: string): TMessage | undefined {
  if (!isValidName(value)) {
    return { key: "settings.error.illegalName" };
  }
}

export function isValidName(value: string): boolean {
  return isNotBlank(value) && value.trim().split(/\s+/).length > 1;
}
